import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { domToBlob } from 'modern-screenshot';
import { firstValueFrom } from 'rxjs';

export const CANVAS_IMAGE_PROXY_URL = new InjectionToken<string>(
  'CANVAS_IMAGE_PROXY_URL'
);

const DESKTOP_WIDTH = 1920;
const DESKTOP_HEIGHT = 1080;
const BROWSER_CHROME_HEIGHT = 27;
const DESKTOP_FRAME_HEIGHT = DESKTOP_HEIGHT + BROWSER_CHROME_HEIGHT;
const MOBILE_WIDTH = 380;
const MOBILE_HEIGHT = 812;

const LABEL_HEIGHT = 32;
const PADDING = 60;
const FRAME_GAP = 20;
const GRID_DOT_CSS =
  'radial-gradient(circle, #b8d9ef 0.8px, transparent 0.8px)';
const FRAME_CONTENT_BG = '#d0d0d0';

interface WrapperState {
  alignItems: string;
  justifyContent: string;
  backgroundColor: string;
}

interface WrapperStates {
  desktop: WrapperState | null;
  mobile: WrapperState | null;
}

@Injectable({ providedIn: 'root' })
export class CanvasScreenshotService {
  private readonly http = inject(HttpClient);
  private readonly proxyBaseUrl = inject(CANVAS_IMAGE_PROXY_URL, {
    optional: true,
  });

  async captureElement(element: HTMLElement): Promise<Blob> {
    const scale = Math.max(2, window.devicePixelRatio);
    const width = PADDING + DESKTOP_WIDTH + FRAME_GAP + MOBILE_WIDTH + PADDING;
    // Mobile column may grow taller than MOBILE_HEIGHT because mobile-responsive
    // popups stack columns vertically and the popup content often exceeds the
    // 812px phone-frame height. Measure it from the live DOM to size the
    // screenshot canvas; otherwise the bottom of the mobile frame gets cropped.
    const mobileContentHeight = this.measureMobilePopupContentHeight(element);
    const height =
      PADDING +
      LABEL_HEIGHT +
      Math.max(DESKTOP_FRAME_HEIGHT, mobileContentHeight) +
      PADDING;

    // The wrapper's flex alignment + overlay background come from `[ngStyle]`
    // on the live element. In single-device mode the
    // `.canvas-viewport.is-single-device …` rule overrides them to flex-start
    // / transparent with !important. `modern-screenshot` bakes those
    // overridden computed values into the clone, but `.canvas-viewport` is an
    // ancestor of the cloned root so the override doesn't apply there. Snap
    // the original ngStyle values now and re-apply them in the clone.
    const wrapperStates = this.readWrapperStates(element);
    const restoreSelection = this.suppressActiveSelection(element);
    const fontStyleEl = await this.injectGoogleFontStyleSheet();
    try {
      return await this.runCaptureElement(
        element,
        width,
        height,
        scale,
        wrapperStates
      );
    } finally {
      restoreSelection();
      fontStyleEl?.remove();
    }
  }

  private async runCaptureElement(
    element: HTMLElement,
    width: number,
    height: number,
    scale: number,
    states: WrapperStates
  ): Promise<Blob> {
    const blob = await domToBlob(element, {
      scale,
      width,
      height,
      type: 'image/png',
      quality: 1,
      filter: (node) => {
        if (node instanceof HTMLElement) {
          const tag = node.tagName?.toLowerCase();
          if (tag === 'fl-visible-mark') {
            return false;
          }
          if (tag === 'fl-image-overlay') {
            return false;
          }
          if (node.classList?.contains('visible-mark')) {
            return false;
          }
          if (node.classList?.contains('cdk-drag-handle')) {
            return false;
          }
          if (node.classList?.contains('ngb-tools')) {
            return false;
          }
          if (node.classList?.contains('block-tools')) {
            return false;
          }
          if (node.classList?.contains('block active')) {
            return false;
          }
          if (
            node.classList?.contains('badge') &&
            node.classList?.contains('position-absolute')
          ) {
            return false;
          }
        }
        return true;
      },
      style: {
        transform: 'none',
        position: 'relative',
        opacity: '1',
      },
      onCloneNode: (cloned: Node) => {
        if (!(cloned instanceof HTMLElement)) {
          return;
        }

        this.applyRootStyles(cloned, width, height);
        this.applyFrameStyles(cloned, states);
      },
      fetchFn: async (url: string) => {
        return this.fetchAsDataUrl(url);
      },
      features: {
        removeControlCharacter: false,
      },
    });

    if (!blob) {
      throw new Error('Failed to capture element as blob');
    }

    return blob;
  }

  async captureWidgetElement(element: HTMLElement): Promise<Blob> {
    const scale = Math.max(2, window.devicePixelRatio);
    const width = element.offsetWidth || 600;
    const height = element.offsetHeight || 400;

    const restoreSelection = this.suppressActiveSelection(element);
    const fontStyleEl = await this.injectGoogleFontStyleSheet();
    try {
      return await this.runCaptureWidgetElement(element, width, height, scale);
    } finally {
      restoreSelection();
      fontStyleEl?.remove();
    }
  }

  private async runCaptureWidgetElement(
    element: HTMLElement,
    width: number,
    height: number,
    scale: number
  ): Promise<Blob> {
    const blob = await domToBlob(element, {
      scale,
      width,
      height,
      type: 'image/png',
      quality: 1,
      style: {
        transform: 'none',
        position: 'relative',
        opacity: '1',
      },
      filter: (node) => {
        if (node instanceof HTMLElement) {
          if (node.classList?.contains('cdk-drag-handle')) {
            return false;
          }
          if (node.classList?.contains('ngb-tools')) {
            return false;
          }
        }
        return true;
      },
      fetchFn: async (url: string) => {
        return this.fetchAsDataUrl(url);
      },
      features: {
        removeControlCharacter: false,
      },
    });

    if (!blob) {
      throw new Error('Failed to capture widget element as blob');
    }

    return blob;
  }

  downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private applyRootStyles(
    root: HTMLElement,
    width: number,
    height: number
  ): void {
    const s = root.style;
    s.setProperty('width', `${width}px`, 'important');
    s.setProperty('height', `${height}px`, 'important');
    s.setProperty('overflow', 'visible', 'important');
    s.setProperty('background-color', '#dff1ff', 'important');
    s.setProperty('background-image', GRID_DOT_CSS, 'important');
    s.setProperty('background-size', '20px 20px', 'important');
  }

  private applyFrameStyles(root: HTMLElement, states: WrapperStates): void {
    const desktopFrame = root.querySelector<HTMLElement>('.desktop-frame');
    const mobileFrame = root.querySelector<HTMLElement>('.mobile-frame');

    const frameTop = `${LABEL_HEIGHT + PADDING}px`;

    if (desktopFrame) {
      this.normalizeDesktopFrame(
        desktopFrame,
        frameTop,
        `${PADDING}px`,
        states.desktop
      );
    }

    if (mobileFrame) {
      this.normalizeMobileFrame(
        mobileFrame,
        frameTop,
        `${PADDING + DESKTOP_WIDTH + FRAME_GAP}px`,
        states.mobile
      );
    }
  }

  private normalizeDesktopFrame(
    frame: HTMLElement,
    top: string,
    left: string,
    state: WrapperState | null
  ): void {
    this.positionFrame(frame, top, left, DESKTOP_WIDTH, DESKTOP_FRAME_HEIGHT);

    const wrapper = frame.querySelector<HTMLElement>('flashy-popup-wrapper');
    if (!wrapper) {
      return;
    }

    let frameContent = frame.querySelector<HTMLElement>('.frame-content');
    let frameViewport = frame.querySelector<HTMLElement>('.frame-viewport');
    const doc = frame.ownerDocument;

    if (!frameContent) {
      // Single-device clone — synthesize the BOTH-mode structure
      // (frame-content > browser-chrome + frame-viewport > wrapper) so the
      // screenshot includes the gray device chrome the user expects.
      frameContent = doc.createElement('div');
      frameContent.className = 'frame-content';
      frameContent.appendChild(this.createBrowserChrome(doc));

      frameViewport = doc.createElement('div');
      frameViewport.className = 'frame-viewport';
      frameContent.appendChild(frameViewport);

      frameViewport.appendChild(wrapper);
      frame.appendChild(frameContent);
    }

    this.styleDesktopFrameContent(frameContent);
    if (frameViewport) {
      this.styleFrameViewport(frameViewport);
    }
    this.styleWrapper(wrapper, DESKTOP_WIDTH, DESKTOP_HEIGHT, 'auto', state);
  }

  private normalizeMobileFrame(
    frame: HTMLElement,
    top: string,
    left: string,
    state: WrapperState | null
  ): void {
    // Mobile is allowed to grow vertically so the full mobile-responsive
    // popup (often taller than 812px because columns stack) is captured
    // without the phone frame clipping the bottom fields off.
    this.positionMobileFrame(frame, top, left, MOBILE_WIDTH, MOBILE_HEIGHT);

    const wrapper = frame.querySelector<HTMLElement>('flashy-popup-wrapper');
    if (!wrapper) {
      return;
    }

    let frameContent = frame.querySelector<HTMLElement>('.frame-content');
    const doc = frame.ownerDocument;

    if (!frameContent) {
      frameContent = doc.createElement('div');
      frameContent.className = 'frame-content';
      frameContent.appendChild(wrapper);
      frame.appendChild(frameContent);
    }

    this.styleMobileFrameContent(frameContent);
    this.styleMobileWrapper(wrapper, MOBILE_WIDTH, MOBILE_HEIGHT, state);
  }

  private positionFrame(
    frame: HTMLElement,
    top: string,
    left: string,
    width: number,
    height: number
  ): void {
    const s = frame.style;
    s.setProperty('top', top, 'important');
    s.setProperty('left', left, 'important');
    s.setProperty('overflow', 'visible', 'important');
    s.setProperty('display', 'block', 'important');
    s.setProperty('width', `${width}px`, 'important');
    s.setProperty('height', `${height}px`, 'important');
  }

  private positionMobileFrame(
    frame: HTMLElement,
    top: string,
    left: string,
    width: number,
    minHeight: number
  ): void {
    const s = frame.style;
    s.setProperty('top', top, 'important');
    s.setProperty('left', left, 'important');
    s.setProperty('overflow', 'visible', 'important');
    s.setProperty('display', 'block', 'important');
    s.setProperty('width', `${width}px`, 'important');
    s.setProperty('height', 'auto', 'important');
    s.setProperty('min-height', `${minHeight}px`, 'important');
  }

  private createBrowserChrome(doc: Document): HTMLElement {
    const chrome = doc.createElement('div');
    chrome.className = 'browser-chrome';
    const cs = chrome.style;
    cs.setProperty('height', `${BROWSER_CHROME_HEIGHT}px`, 'important');
    cs.setProperty('flex-shrink', '0', 'important');
    cs.setProperty('background', 'white', 'important');
    cs.setProperty('padding', '8px 16px', 'important');
    cs.setProperty('box-sizing', 'border-box', 'important');
    cs.setProperty(
      'border-bottom',
      '1px solid rgba(0, 0, 0, 0.04)',
      'important'
    );
    cs.setProperty('display', 'flex', 'important');
    cs.setProperty('align-items', 'center', 'important');
    cs.setProperty('gap', '16px', 'important');

    for (let i = 0; i < 3; i++) {
      const dot = doc.createElement('span');
      dot.className = 'traffic-dot';
      const ds = dot.style;
      ds.setProperty('width', '10px', 'important');
      ds.setProperty('height', '10px', 'important');
      ds.setProperty('background', FRAME_CONTENT_BG, 'important');
      ds.setProperty('border-radius', '50%', 'important');
      ds.setProperty('display', 'block', 'important');
      ds.setProperty('flex-shrink', '0', 'important');
      chrome.appendChild(dot);
    }
    return chrome;
  }

  private styleDesktopFrameContent(el: HTMLElement): void {
    const s = el.style;
    s.setProperty('width', '100%', 'important');
    s.setProperty('height', '100%', 'important');
    s.setProperty('background', FRAME_CONTENT_BG, 'important');
    s.setProperty('overflow', 'hidden', 'important');
    s.setProperty('display', 'flex', 'important');
    s.setProperty('flex-direction', 'column', 'important');
    s.setProperty('border-radius', '12px', 'important');
    s.setProperty('box-shadow', '0 0 0 5px white', 'important');
  }

  private styleMobileFrameContent(el: HTMLElement): void {
    const s = el.style;
    s.setProperty('width', '100%', 'important');
    s.setProperty('height', 'auto', 'important');
    s.setProperty('min-height', `${MOBILE_HEIGHT}px`, 'important');
    s.setProperty('background', FRAME_CONTENT_BG, 'important');
    s.setProperty('overflow', 'hidden', 'important');
    s.setProperty('border-radius', '32px', 'important');
    s.setProperty('box-shadow', '0 0 0 5px white', 'important');
  }

  private styleFrameViewport(el: HTMLElement): void {
    const s = el.style;
    s.setProperty('flex', '1', 'important');
    s.setProperty('overflow', 'visible', 'important');
    s.setProperty('position', 'relative', 'important');
  }

  private styleMobileWrapper(
    wrapper: HTMLElement,
    width: number,
    minHeight: number,
    state: WrapperState | null
  ): void {
    const ws = wrapper.style;
    // Sized at the natural popup-wrap content height (min = phone-frame
    // height) so all mobile-responsive form fields are visible. The phone
    // frame in the screenshot grows to match.
    ws.setProperty('position', 'relative', 'important');
    ws.setProperty('top', 'auto', 'important');
    ws.setProperty('left', 'auto', 'important');
    ws.setProperty('width', `${width}px`, 'important');
    ws.setProperty('height', 'auto', 'important');
    ws.setProperty('max-height', 'none', 'important');
    ws.setProperty('min-height', `${minHeight}px`, 'important');
    ws.setProperty('display', 'flex', 'important');
    ws.setProperty('overflow-x', 'hidden', 'important');
    ws.setProperty('overflow-y', 'visible', 'important');

    this.applyWrapperState(ws, state);
  }

  private styleWrapper(
    wrapper: HTMLElement,
    width: number,
    height: number,
    overflowY: 'auto' | 'hidden',
    state: WrapperState | null
  ): void {
    const ws = wrapper.style;
    ws.setProperty('position', 'absolute', 'important');
    ws.setProperty('top', '0', 'important');
    ws.setProperty('left', '0', 'important');
    ws.setProperty('width', `${width}px`, 'important');
    ws.setProperty('height', `${height}px`, 'important');
    ws.setProperty('max-height', `${height}px`, 'important');
    ws.setProperty('min-height', `${height}px`, 'important');
    ws.setProperty('display', 'flex', 'important');
    ws.setProperty('overflow-x', 'hidden', 'important');
    ws.setProperty('overflow-y', overflowY, 'important');

    this.applyWrapperState(ws, state);
  }

  private applyWrapperState(
    ws: CSSStyleDeclaration,
    state: WrapperState | null
  ): void {
    if (!state) {
      return;
    }
    if (state.alignItems) {
      ws.setProperty('align-items', state.alignItems, 'important');
    }
    if (state.justifyContent) {
      ws.setProperty('justify-content', state.justifyContent, 'important');
    }
    // `.is-single-device` forces transparent in the live DOM and that gets
    // baked into the clone. Restore the ngStyle-set overlay color
    // (defaulting to transparent so the gray frame-content shows when no
    // overlay is configured).
    ws.setProperty(
      'background-color',
      state.backgroundColor || 'transparent',
      'important'
    );
  }

  private measureMobilePopupContentHeight(root: HTMLElement): number {
    // popup-block is the inner stack of column/element children. With
    // mobile-responsive layout the columns stack vertically and the
    // `:not(.mobile-responsive)` max-height clamp doesn't apply, so its
    // offsetHeight is the natural full height of the mobile popup content.
    const block = root.querySelector<HTMLElement>('.mobile-frame .popup-block');
    if (!block) {
      return MOBILE_HEIGHT;
    }
    return Math.max(MOBILE_HEIGHT, block.scrollHeight);
  }

  private readWrapperStates(root: HTMLElement): WrapperStates {
    const read = (selector: string): WrapperState | null => {
      const el = root.querySelector<HTMLElement>(selector);
      if (!el) {
        return null;
      }
      return {
        alignItems: el.style.alignItems,
        justifyContent: el.style.justifyContent,
        backgroundColor: el.style.backgroundColor,
      };
    };
    return {
      desktop: read('.desktop-frame flashy-popup-wrapper'),
      mobile: read('.mobile-frame flashy-popup-wrapper'),
    };
  }

  private async injectGoogleFontStyleSheet(): Promise<HTMLStyleElement | null> {
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
    ).filter((link) => /fonts\.googleapis\.com/.test(link.href));

    if (!links.length) {
      return null;
    }

    const seen = new Set<string>();
    const cssTexts = await Promise.all(
      links.map(async (link) => {
        if (seen.has(link.href)) {
          return '';
        }
        seen.add(link.href);
        try {
          const res = await fetch(link.href, { mode: 'cors' });
          return res.ok ? await res.text() : '';
        } catch {
          return '';
        }
      })
    );

    const combined = cssTexts.filter(Boolean).join('\n');
    if (!combined) {
      return null;
    }

    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-screenshot-fonts', '');
    styleEl.textContent = combined;
    document.head.appendChild(styleEl);
    return styleEl;
  }

  private suppressActiveSelection(root: HTMLElement): () => void {
    const activeBlocks = Array.from(
      root.querySelectorAll<HTMLElement>('.block.active')
    );
    activeBlocks.forEach((el) => el.classList.remove('active'));
    return () => activeBlocks.forEach((el) => el.classList.add('active'));
  }

  private async fetchAsDataUrl(url: string): Promise<string | false> {
    if (!this.proxyBaseUrl) {
      return false;
    }
    try {
      const proxyUrl = `${this.proxyBaseUrl}/actions?action=imageProxy&url=${encodeURIComponent(url)}`;
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: string }>(proxyUrl)
      );
      if (!response.success) {
        return false;
      }
      return response.data;
    } catch {
      return false;
    }
  }
}
